import { memo, useEffect, useRef, useState } from "react";
import Popover from "../popover/popover";
import SettingsIcon from "./settingsIcon";
import { useAppDispatch, useAppSelector } from "redux-share/hooks";
import { setClearPopoversSlice, setSettingPopover, setSharePopover } from "redux-share/slices/popoversSlice";
import useOnClickOutside from "helpers/hooks/useOnClickOutside";
import { conditions } from "helpers";
import { ConditionName, SettingSections } from "interfaces";
import { setUserSettings } from "redux-share/slices/userSlice";
import { createSettings } from "redux-share/actions/user.actions";
import { deActiveSettingLog, sendFluentLog } from "../../../../../logs";
import { handlePopoverSizeInMiniPlayer } from "helpers/helpers";
import { shallowEqual } from "react-redux";
import ControllbarTooltip from "../../mainComponent/footer/controllbar/controllbarTooltip";
import Toggle from "../toggle/toggle";
import QualityLevels from "../qualityLevels/qualityLevels";
import MultiAudio from "../multiAudio/multiAudio";
import SubtitlesMenu from "../subtitles/subtitlesMenu";
import SubtitlesBackgroundColorsMenu from "../subtitles/subtitlesBackgroundColorsMenu";
import SubtitlesTextColorsMenu from "../subtitles/subtitlesTextColorsMenu";
import SubtitleSettingMenu from "../subtitles/subtitleSettingMenu";
import ChevronRightIcon from "../../icon-components/chevronRight";

const Settings = () => {
    const dispatch = useAppDispatch();
    const { videoElementSizeInfo } = useAppSelector(
        ({ videoData: { params, videoElementSizeInfo } }) => ({ params, videoElementSizeInfo }),
        shallowEqual
    );
    const { settingPopover } = useAppSelector(({ popovers: { settingPopover } }) => ({ settingPopover }), shallowEqual);
    const { settings } = useAppSelector(({ user: { settings } }) => ({ settings }), shallowEqual);
    const { qualityLevels, manualQuality, autoQuality, showMiniPlayer } = useAppSelector(
        ({ controller: { qualityLevels, manualQuality, autoQuality, showMiniPlayer } }) => ({
            qualityLevels,
            manualQuality,
            autoQuality,
            showMiniPlayer
        }),
        shallowEqual
    );

    const [section, setSection] = useState<string>(SettingSections.main);
    const [elementHeight, setElementHeight] = useState<number>(0);
    const [showTooltip, setShowTooltip] = useState<boolean>(false);

    const settingsRef = useRef(null);

    useOnClickOutside(settingsRef, () => {
        dispatch(setSettingPopover(false));
        setTimeout(() => setSection(SettingSections.main), 500);
    });

    useEffect(() => {
        if (conditions(ConditionName.showMiniPlayer)) setSection(SettingSections.qualityLevels);
        setElementHeight(document.getElementById(section)?.offsetHeight!);
    }, [section, showMiniPlayer]);

    const handleQualityLabel = () => {
        const quality = conditions(ConditionName.isAutoQuality)
            ? Number(qualityLevels[autoQuality!])
            : Number(qualityLevels[manualQuality !== undefined ? manualQuality : settings.quality]);
        if (quality >= 720 && quality < 1080) return "HD";
        else if (quality >= 1080 && quality < 2160) return "FHD";
        else if (quality >= 2160 && quality < 4320) return "4K";
        else if (quality >= 4320) return "8K";
        else return `${quality}p`;
    };

    const handleToggle = () => {
        dispatch(setUserSettings({ ...settings, poll_emoji: !settings.poll_emoji }));
        createSettings({ ...settings, poll_emoji: !settings.poll_emoji });
        sendFluentLog(deActiveSettingLog());
    };

    const handleShowSettingPopover = () => {
        if (conditions(ConditionName.isComplexPortrait)) dispatch(setClearPopoversSlice());
        dispatch(setSettingPopover(!settingPopover));
    };

    return (
        <div className={`pl-inline-flex ${conditions(ConditionName.isLandscape) && "pl-relative"}`} ref={settingsRef}>
            <div
                className={`pl-flex pl-items-center pl-relative pl-cursor-pointer`}
                onClick={handleShowSettingPopover}
                onMouseOver={() => !conditions(ConditionName.isTouchScreen) && setShowTooltip(true)}
                onMouseLeave={() => !conditions(ConditionName.isTouchScreen) && setShowTooltip(false)}
            >
                <SettingsIcon />
                {(!conditions(ConditionName.isMiniPlayer) || conditions(ConditionName.isFullMobilePortrait)) && (
                    <span
                        className={`${
                            Number(
                                qualityLevels[
                                    conditions(ConditionName.isAutoQuality)
                                        ? autoQuality!
                                        : manualQuality !== undefined
                                        ? manualQuality
                                        : settings.quality
                                ]
                            ) >= 720
                                ? "pl-bg-red-light pl-right-[-6px]"
                                : "pl-bg-secondary-500 pl-right-[-10px]"
                        } pl-absolute pl-top-0 ${
                            conditions(ConditionName.isComplexPortrait) && "pl-top-[-4px] pl-right-[-16px]"
                        } pl-text-[8px] pl-text-white pl-px-1 pl-pt-[1px]`}
                    >
                        {handleQualityLabel()}
                    </span>
                )}
                {showTooltip && <ControllbarTooltip title="تنظیمات" />}
            </div>
            <Popover
                classNames={`!pl-p-0 pl-overflow-hidden !pl-bg-white dark:!pl-bg-dark-13 !pl-rounded-[4px] ${
                    !conditions(ConditionName.isComplexPortrait) && "pl-mb-[33px] pl-right-0"
                } ${conditions(ConditionName.isFullMobilePortrait) && "!pl-w-screen"} ${
                    conditions(ConditionName.isMiniPlayer) && !conditions(ConditionName.isFullMobilePortrait)
                        ? `!pl-fixed pl-rounded-r-none pl-w-[250px] !pl-mb-0`
                        : "pl-w-[285px]"
                }`}
                animation={
                    conditions(ConditionName.isMiniPlayer) && !conditions(ConditionName.isFullMobilePortrait)
                        ? settingPopover
                            ? "animate__fadeInRight"
                            : "animate__fadeOutRight"
                        : undefined
                }
                styles={{
                    maxHeight:
                        conditions(ConditionName.isMiniPlayer) && !conditions(ConditionName.isFullMobilePortrait)
                            ? `${videoElementSizeInfo.height}px`
                            : `${elementHeight}px`,
                    ...handlePopoverSizeInMiniPlayer()
                }}
                status={settingPopover}
                onClose={() => dispatch(setSettingPopover(false))}
                type={conditions(ConditionName.isComplexPortrait) ? "modal" : "popover"}
                isBlurBackground={conditions(ConditionName.isComplexPortrait)}
                ref={settingsRef}
            >
                <>
                    {section === SettingSections.main && (
                        <div id="main" className="pl-px-4 pl-rounded-[10px] pl-flex pl-flex-col pl-py-3 animate__animated animate__fadeIn">
                            <p
                                className={`pl-pr-2 pl-text-[14px] pl-font-bold pl-border-b pl-pb-3 pl-text-black dark:pl-text-gray-100 pl-border-b-gray-300 dark:pl-border-b-dark-4`}
                            >
                                تنظیمات
                            </p>
                            {!conditions(ConditionName.isStaticFormat) && (
                                <>
                                    <p
                                        className={`pl-pr-2 pl-py-[10px] pl-cursor-pointer pl-text-[12px] pl-font-medium pl-text-black dark:pl-text-gray-100 hover:pl-bg-main/[0.1] hover:dark:pl-bg-white/[0.25] pl-rounded-[4px]`}
                                        onClick={() => setSection(SettingSections.qualityLevels)}
                                    >
                                        انتخاب کیفیت
                                    </p>
                                    {conditions(ConditionName.hasSubtitle) && (
                                        <div
                                            onClick={() => setSection(SettingSections.subtitlesSettingMenu)}
                                            className="pl-flex pl-items-center pl-justify-between pl-px-2 pl-py-[10px] pl-cursor-pointer pl-text-[12px] pl-text-black dark:pl-text-gray-100 hover:pl-bg-main/[0.1] hover:dark:pl-bg-white/[0.25] pl-rounded-[4px]"
                                        >
                                            <p className={`pl-text-[12px]`}>تنظیمات زیرنویس</p>
                                            <div className={`pl-rounded-[4px] pl-p-1 pl-rotate-180 pl-bg-gray-300 dark:pl-bg-dark-4`}>
                                                <ChevronRightIcon className={`pl-w-3 pl-h-3 pl-stroke-gray-800`} />
                                            </div>
                                        </div>
                                    )}
                                    {conditions(ConditionName.hasMultiAudio) && (
                                        <p
                                            className={`pl-pr-2 pl-py-[10px] pl-cursor-pointer pl-text-[12px] pl-font-medium pl-text-black dark:pl-text-gray-100 hover:pl-bg-main/[0.1] hover:dark:pl-bg-white/[0.25] pl-rounded-[4px]`}
                                            onClick={() => setSection(SettingSections.multiAudio)}
                                        >
                                            انتخاب زبان
                                        </p>
                                    )}
                                    {conditions(ConditionName.showShareInSetting) && (
                                        <p
                                            className={`pl-pr-2 pl-py-[10px] pl-cursor-pointer pl-text-[12px] pl-font-medium pl-text-black dark:pl-text-gray-100 hover:pl-bg-main/[0.1] hover:dark:pl-bg-white/[0.25] pl-rounded-[4px]`}
                                            onClick={() => dispatch(setSharePopover(true))}
                                        >
                                            اشتراک گذاری
                                        </p>
                                    )}
                                </>
                            )}

                            {conditions(ConditionName.isLiveParam) && (
                                <div className="pl-flex pl-justify-between pl-items-center pl-pr-2">
                                    <span className="pl-text-[12px] pl-font-medium pl-ml-10">نمايش نظرسنجى و ايموجى ها</span>
                                    <Toggle enabled={settings.poll_emoji} setEnabled={handleToggle} />
                                </div>
                            )}
                        </div>
                    )}
                    {section === SettingSections.qualityLevels && !conditions(ConditionName.isStaticFormat) && (
                        <QualityLevels setSection={setSection} />
                    )}
                    {section === SettingSections.multiAudio && <MultiAudio setSection={setSection} />}
                    {section === SettingSections.subtitlesSettingMenu && <SubtitleSettingMenu setSection={setSection} />}
                    {section === SettingSections.subtitles && <SubtitlesMenu setSection={setSection} />}
                    {section === SettingSections.subtitlesBackgroundColors && <SubtitlesBackgroundColorsMenu setSection={setSection} />}
                    {section === SettingSections.subtitlesTextColors && <SubtitlesTextColorsMenu setSection={setSection} />}
                </>
            </Popover>
        </div>
    );
};

export default memo(Settings);
